import React, { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { formatDate } from "utils/date";
import { useDialogShadow } from "@evergis/ui";
import { GeometryType, ResourceInfoDc } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { LayerAttribute } from "components/Dialogs/EditResource/components/Layer/LayerAttributeItem";
import { useLayerInfoWithUpdate } from "components/Dialogs/EditResource/hooks/useLayerInfoWithUpdate";
import { getMetaFromResource } from "hooks/useResourceMeta";
import { dialogResourceSelector } from "ducks/dialogs/selectors";
import { getProjectName } from "ducks/project/selectors";
import { useCurrentProjectWithAddedLayers, useIsLayerInProject } from "hooks/project/useAddLayersToCurrentProject";
import { getTableAlias } from "../../../../../evergis/core/table/getTableInfo";
import { Attributes } from "../../../DialogComponents/Attributes/Attributes";
import { DialogActions } from "../../../DialogComponents/DialogActions";
import { DialogTitle } from "../../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../../DialogComponents/FullScreenDialog";
import { ResourceMeta } from "../../../DialogComponents/ResourceMeta/ResourceMeta";
import { DialogContent, ProjectMetaBlock, RestrictedWidthContainer } from "../../../DialogComponents/styled";
import { TableSourceDescription } from "../../../DialogComponents/TableSourceDescription";
import { DataField, FieldValue, styledHeaderText } from "../../../styled";
import { useDialogReset } from "../../../../../hooks/useDialogs";
import { useLayerAttributes } from "../../hooks/useLayerAttributes";
import { LayerAttributeItem } from "./LayerAttributeItem";
import { isLocationMainSelector } from "../../../../../ducks/location/selectors";

export const LayerInfo: FC = () => {
  const { t } = useTranslation();
  const isProjectLocation = useSelector(isLocationMainSelector);
  const projectIsOpened = !!useSelector(getProjectName);
  const currentResource = useSelector(dialogResourceSelector);
  const [{ table, layer, loading, previewUrl }] = useLayerInfoWithUpdate();
  const attributes = useLayerAttributes(layer.layerDefinition);
  const meta = getMetaFromResource(layer as ResourceInfoDc, previewUrl);
  const { alias, owner, changedDate, geometryType, layerDefinition } = layer;
  const displayField = layerDefinition && (layerDefinition.titleAttribute || layerDefinition.idAttribute);
  const { shadows, setScrollContainer } = useDialogShadow();
  const ableAddToProject = !useIsLayerInProject(currentResource) && !isProjectLocation && projectIsOpened;
  const cancelDialog = useDialogReset();
  const addLayerToCurrentProject = useCurrentProjectWithAddedLayers([layer as ResourceInfoDc]);

  const handleAddLayerToProject = useCallback(() => {
    addLayerToCurrentProject();
    cancelDialog();
  }, [addLayerToCurrentProject, cancelDialog]);

  return (
    <FullScreenDialog
      dialogContext={{
        title: {
          icon: "info",
        },
        applyText: t("actions.ok", { ns: "common" }),
        attributes: {
          label: t("infoResource.attributes", { ns: "dialogs" }),
          description: t("infoResource.attributesLayerDecription", { ns: "dialogs" }),
          icon: "table",
        },
      }}
    >
      <DialogTitle
        titleText={styledHeaderText(alias, t("properties", { ns: "common" }))}
        displayShadow={shadows.upper}
      />
      <DialogContent ref={setScrollContainer}>
        <RestrictedWidthContainer>
          <ProjectMetaBlock>
            <ResourceMeta meta={meta} readOnly>
              <DataField column>
                <TableSourceDescription
                  tableName={getTableAlias(table)}
                  layerName={alias}
                  geometryType={geometryType as GeometryType}
                />
                <FieldValue field={t("infoResource.owner", { ns: "dialogs" })} value={owner} />
                <FieldValue field={t("infoResource.layerUpdated", { ns: "dialogs" })} value={formatDate(changedDate)} />
              </DataField>
            </ResourceMeta>
          </ProjectMetaBlock>
        </RestrictedWidthContainer>
        <Attributes attributes={attributes} draggable={false} loading={loading}>
          {(attribute: LayerAttribute) => <LayerAttributeItem attribute={attribute} displayField={displayField} />}
        </Attributes>
      </DialogContent>
      <DialogActions
        displayShadow={shadows.bottom}
        applyText={"ок"}
        cancelButtonText={ableAddToProject && t("infoResource.addToMap", { ns: "dialogs" })}
        onCancel={ableAddToProject && handleAddLayerToProject}
      />
    </FullScreenDialog>
  );
};
